"use client"

import { payment } from "@/actions/payment/client/payment"
import { type Address, type BillingAddress } from "@/actions/payment/types"
import { replaceScriptNode } from "@/app/(site)/[locale]/checkout/centra/utils/replaceScriptNode"
import { useStepValidation } from "@/app/(site)/[locale]/checkout/CheckoutContent/Steps/stepValidation"
import useSWRBasketSessionState from "@/app/(site)/_utils/_hooks/SWR/useSWRBasketSessionState"
import useEcommerceStore from "@/app/(site)/contexts/ecommerce/store"
import logger from "@/lib/logger/logger-init"
import {
    baseUrl,
    PAYMENT_FAILED_URL,
    REQUIRED_FIELDS,
    successUrl
} from "@/lib/siteconfig/constants"
import { triggerPaymentResultEvent } from "@/lib/utils/Events/helpers"
import { useSiteContext } from "@/lib/utils/tracking/siteContext"
import { useAppForCurrentLocale } from "@/src/utils/locale/useAppForCurrentLocale"
import { useRouter } from "next/navigation"
import { memo, type RefObject, useEffect, useEffectEvent, useRef } from "react"

export interface AdyenDropInScriptProps {
    onError: ({ message }: { message: string }) => void
    onLoaded: () => void
    htmlAdyenDropInReference?: RefObject<HTMLDivElement | null>
    langCode: string
}

type CentraPaymentActionResult = {
    action?: string | null
    url?: string
    /** Checkout API: code. Some GraphQL examples use script — both dispatch the response event. */
    code?: string
    script?: string
    formFields?: Record<string, unknown> | null
    order?: { resultCode?: string | null } | null
    errors?: unknown[] | null
}

/** Long enough for a slow paymentReturnPage POST to commit before we compete with it. */
const SUCCESS_REDIRECT_FALLBACK_MS = 10000

function dispatchCentraPaymentResponse(detail: Record<string, unknown>) {
    document.dispatchEvent(
        new CustomEvent("centra_checkout_payment_response", { detail })
    )
}

/**
 * Handles Centra payment actions for Adyen Drop-in.
 * On success/javascript, respond via centra_checkout_payment_response so Centra's
 * injected Drop-in can continue (handleAction / redirect) — do not hard-redirect on success.
 * @see https://centra.dev/integrations/payments/adyen-drop-in
 */
function handleCentraPaymentAction(paymentResponseData: CentraPaymentActionResult) {
    const errors = paymentResponseData.errors
    if (errors && errors.length > 0) {
        const firstError = errors[0]
        throw new Error(typeof firstError === "string" ? firstError : "Payment failed")
    }

    const formFields = (paymentResponseData.formFields ?? {}) as Record<string, unknown>
    const action = (paymentResponseData.action ?? "").toLowerCase()
    const legacySnippet = paymentResponseData.code ?? paymentResponseData.script

    switch (action) {
        case "success": {
            // Docs: dispatch formFields (e.g. { resultCode: "Authorised" }).
            // Centra scripts navigate after the event — do not redirect here.
            const detail =
                Object.keys(formFields).length > 0
                    ? formFields
                    : {
                          action: null,
                          resultCode: paymentResponseData.order?.resultCode ?? null
                      }

            dispatchCentraPaymentResponse(detail)
            break
        }

        case "redirect": {
            if (!paymentResponseData.url) {
                throw new Error(
                    'centraCheckoutPaymentCallback: action "redirect" missing url'
                )
            }
            window.location.href = paymentResponseData.url
            break
        }

        case "javascript": {
            // Prefer formFields event (Drop-in listens and calls handleAction when detail.action exists).
            // Fallback: eval code/script which typically dispatches the same event.
            if (Object.keys(formFields).length > 0) {
                dispatchCentraPaymentResponse(formFields)
            } else if (legacySnippet) {
                eval(legacySnippet)
            } else {
                throw new Error(
                    'centraCheckoutPaymentCallback: action "javascript" missing formFields and code/script'
                )
            }
            break
        }

        default: {
            throw new Error(
                `centraCheckoutPaymentCallback: unsupported action "${paymentResponseData.action}"`
            )
        }
    }
}

/**
 * compareCentraAddressEquality
 * Compare two Centra addresses to see if they are equal. Some keys have special handling.
 *
 * @param basketAddress
 * @param basketShippingAddress
 * @returns {boolean} - Will be true if the addresses are equal, false if they are not.
 */
export const compareCentraAddressEquality = (
    basketAddress?: Partial<BillingAddress> & { newsletter?: boolean },
    basketShippingAddress?: Partial<Address>
) => {
    if (!basketAddress && !basketShippingAddress) {
        return true
    }

    const excludeKeysFromCheck = ["loggedIn", "newsletter"]
    return Object.keys(basketAddress ?? {}).every(key => {
        if (excludeKeysFromCheck.includes(key)) {
            return true
        }

        return (
            basketAddress?.[key as keyof BillingAddress] ===
            basketShippingAddress?.[key as keyof Address]
        )
    })
}

export const AdyenDropInScript = memo(
    ({
        onLoaded,
        onError,
        htmlAdyenDropInReference,
        langCode
    }: AdyenDropInScriptProps): null => {
        const { setCheckoutCurrentStep } = useEcommerceStore()
        const { validateSteps } = useStepValidation()

        const { selection, checkoutApiSelection, mutate } = useSWRBasketSessionState()
        const paymentProcessingRef = useRef(false)
        const paymentCompletedRef = useRef(false)
        const successRedirectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

        const router = useRouter()
        const { path: localePath } = useAppForCurrentLocale()
        const siteContext = useSiteContext()

        const errorUrl = `${baseUrl}${localePath}/${PAYMENT_FAILED_URL}`

        const getAddressData = () => {
            if (!selection) {
                return null
            }

            const shippingAddressData = {
                email: selection.shippingAddress?.email || "",
                phoneNumber: selection.shippingAddress?.phoneNumber || "",
                firstName: selection.shippingAddress?.firstName || "",
                lastName: selection.shippingAddress?.lastName || "",
                address1: selection.shippingAddress?.address1 || "",
                state: selection.shippingAddress?.state || "",
                address2: selection.shippingAddress?.address2 || "",
                zipCode: selection.shippingAddress?.zipCode || "",
                city: selection.shippingAddress?.city || "",
                country: selection.shippingAddress?.country || ""
            }

            let billingAddress: Address = {
                newsletter: selection.address?.newsletter || false,
                ...shippingAddressData
            }

            const sameShippingAddressData = compareCentraAddressEquality(
                selection.address,
                selection.shippingAddress
            )

            if (!sameShippingAddressData) {
                billingAddress = {
                    newsletter: selection.address?.newsletter || false,
                    email: selection.address?.email
                        ? selection.address.email
                        : shippingAddressData.email,
                    phoneNumber: selection.address?.phoneNumber
                        ? selection.address.phoneNumber
                        : shippingAddressData.phoneNumber,
                    firstName: selection.address?.firstName
                        ? selection.address.firstName
                        : shippingAddressData.firstName,
                    lastName: selection.address?.lastName
                        ? selection.address.lastName
                        : shippingAddressData.lastName,
                    address1: selection.address?.address1
                        ? selection.address.address1
                        : shippingAddressData.address1,
                    state: selection.address?.state || "",
                    address2: selection.address?.address2
                        ? selection.address.address2
                        : shippingAddressData.address2,
                    zipCode: selection.address?.zipCode
                        ? selection.address.zipCode
                        : shippingAddressData.zipCode,
                    city: selection.address?.city
                        ? selection.address.city
                        : shippingAddressData.city,
                    country: selection.address?.country
                        ? selection.address.country
                        : shippingAddressData.country
                }
            }

            const hasRequiredFields = (addr: typeof shippingAddressData | Address) =>
                REQUIRED_FIELDS.every(field => Boolean(addr[field as keyof typeof addr]))

            const isValid =
                hasRequiredFields(shippingAddressData) &&
                hasRequiredFields(billingAddress)

            return { shippingAddressData, billingAddress, isValid }
        }

        const getPayment = async () => {
            if (paymentProcessingRef.current || paymentCompletedRef.current) {
                return
            }

            if (!htmlAdyenDropInReference?.current || !selection) {
                return
            }

            if (!selection.items || selection.items.length === 0) {
                logger.warn("Cannot initialize payment - cart is empty")
                return
            }

            try {
                const addressData = getAddressData()
                if (!addressData) {
                    throw new Error("Address data not available")
                }
                const { shippingAddressData, billingAddress } = addressData

                const params: any = {
                    paymentMethod: selection.paymentMethod,
                    paymentReturnPage: successUrl,
                    paymentFailedPage: errorUrl,
                    termsAndConditions: true,
                    address: billingAddress,
                    shippingAddress: shippingAddressData,
                    language: langCode
                }

                const response = await payment(params)
                await mutate()
                if (response._tag === "failure") {
                    logger.error("Payment initiation failed", response.error)
                    throw new Error("Payment initiation failed")
                }

                const formHtml = response.data?.paymentResult?.formHtml
                if (formHtml && htmlAdyenDropInReference.current) {
                    htmlAdyenDropInReference.current.innerHTML = formHtml
                    replaceScriptNode(htmlAdyenDropInReference.current)
                    onLoaded()
                }

                const hasErrors = (response.data?.paymentResult?.errors?.length ?? 0) > 0
                if (hasErrors) {
                    onError({ message: "Out of stock" })
                }
            } catch (error) {
                logger.error("Error in AdyenDropInScript", error)
                onError({ message: "Error in AdyenDropInScript" })
            }
        }

        /**
         * Centra's script owns the redirect after centra_checkout_payment_response, but a
         * success response carries no `detail.action` — the only thing the documented
         * listener reacts to. The order is authorised by the time we get here, so the worst
         * outcome to design against is a customer stranded on the payment form. If Centra
         * navigates first the page unloads and this never fires; if it fires, the warning is
         * what tells us the script isn't redirecting.
         */
        const scheduleSuccessRedirectFallback = () => {
            successRedirectTimerRef.current = setTimeout(() => {
                logger.warn(
                    "Centra did not redirect after payment success — navigating to the payment result",
                    { delayMs: SUCCESS_REDIRECT_FALLBACK_MS }
                )
                window.location.href = successUrl
            }, SUCCESS_REDIRECT_FALLBACK_MS)
        }

        const centraCheckoutPaymentCallback = async (origData: any) => {
            if (!origData?.detail) {
                return
            }

            const postData = origData.detail

            if (!selection) {
                logger.error("Selection is null in payment callback")
                onError({ message: "Selection data not available" })
                return
            }

            // Mark payment as processing to prevent re-initialization
            paymentProcessingRef.current = true
            triggerPaymentResultEvent(true)

            try {
                // Docs: if addressIncluded is false (paymentInitiateOnly init), append
                // checkout addresses. If true, event may include them — we still use the
                // validated selection addresses as the source of truth for Checkout API.
                const addressData = getAddressData()
                if (!addressData || !addressData.isValid) {
                    throw new Error("Address data not available")
                }

                const { shippingAddressData, billingAddress } = addressData
                const params: any = {
                    paymentMethod: selection.paymentMethod,
                    paymentReturnPage: successUrl,
                    paymentFailedPage: errorUrl,
                    termsAndConditions: true,
                    address: billingAddress,
                    shippingAddress: shippingAddressData,
                    language: langCode
                }

                // tracking. Optional: an unexpected shape must not abort a payment the
                // customer has already authorised.
                const dropInPaymentMethod =
                    postData.paymentMethodSpecificFields?.paymentMethod?.type
                if (
                    dropInPaymentMethod &&
                    checkoutApiSelection &&
                    checkoutApiSelection.selection
                ) {
                    await import("@/lib/utils/tracking/gtmEvents").then(
                        ({ addPaymentInfoEvent }) =>
                            addPaymentInfoEvent(
                                {
                                    ...checkoutApiSelection,
                                    selection: {
                                        ...checkoutApiSelection.selection,
                                        paymentMethodName: dropInPaymentMethod
                                    }
                                },
                                siteContext
                            )
                    )
                }

                if (postData.paymentMethodSpecificFields) {
                    params.paymentMethodSpecificFields =
                        postData.paymentMethodSpecificFields
                }

                // Call Centra payment API with the encrypted payment data
                const paymentResponse = await payment(params)

                if (paymentResponse._tag === "failure") {
                    const errorMsg = paymentResponse.error.message || "Payment failed"
                    logger.error("Payment callback failed", errorMsg)
                    throw new Error(errorMsg)
                }

                const paymentData = paymentResponse.data
                if (!paymentData || !paymentData.paymentResult) {
                    throw new Error("Payment result data was falsy")
                }

                // tracking
                if (dropInPaymentMethod) {
                    localStorage.setItem(
                        "latest-accepted-payment-method",
                        dropInPaymentMethod
                    )
                }

                if (postData.responseEventRequired) {
                    const paymentResult =
                        paymentData.paymentResult as unknown as CentraPaymentActionResult

                    handleCentraPaymentAction(paymentResult)

                    // Centra scripts navigate after centra_checkout_payment_response.
                    // Do not hard-redirect on success (Adyen v6 / Drop-in docs).
                    if ((paymentResult.action ?? "").toLowerCase() === "success") {
                        paymentCompletedRef.current = true
                        scheduleSuccessRedirectFallback()
                    }
                }
            } catch (error) {
                await mutate()
                paymentProcessingRef.current = false
                logger.error("Error in centraCheckoutPaymentCallback", {
                    error,
                    message: error instanceof Error ? error.message : String(error),
                    stack: error instanceof Error ? error.stack : undefined
                })
                onError({
                    message: error instanceof Error ? error.message : String(error)
                })
            }
        }

        useEffect(() => {
            if (selection) {
                const addressData = getAddressData()
                if (!addressData || !addressData.isValid) {
                    setCheckoutCurrentStep(1)
                    return
                }
                const validation = validateSteps([1, 2])
                if (!validation.isValid && validation.firstErrorStep) {
                    setCheckoutCurrentStep(validation.firstErrorStep)
                    return
                }

                getPayment()
            }
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [])

        /**
         * The listener has to see the current selection without being torn down and
         * rebuilt whenever it changes — re-subscribing mid-payment is how a callback
         * gets missed. An Effect Event is always current, so the effect can mount once.
         */
        const onPaymentCallback = useEffectEvent((event: Event) => {
            void centraCheckoutPaymentCallback(event)
        })

        useEffect(() => {
            const listener = (event: Event) => onPaymentCallback(event)
            document.addEventListener("centra_checkout_payment_callback", listener)

            return () => {
                document.removeEventListener("centra_checkout_payment_callback", listener)
            }
        }, [])

        useEffect(() => {
            const clearSuccessRedirectFallback = () => {
                if (!successRedirectTimerRef.current) return

                clearTimeout(successRedirectTimerRef.current)
                successRedirectTimerRef.current = null
            }

            window.addEventListener("pagehide", clearSuccessRedirectFallback)

            return () => {
                window.removeEventListener("pagehide", clearSuccessRedirectFallback)
                clearSuccessRedirectFallback()
            }
        }, [])

        return null
    }
)

AdyenDropInScript.displayName = "AdyenDropInScript"
